// Galaxia Pro — Primitives (ESM, typed)
// Mirror of `../primitives.jsx` plus new primitives required by
// the landing+login feature: Skeleton, GxDrawer, GxAvatar.

import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type JSX,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";

import { Icon, type IconName } from "./icons";
import { cls, fmtCOP } from "./utils";

// ── GxButton ───────────────────────────────────────────────

export interface GxButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: "primary" | "accent" | "ghost" | "soft" | "danger";
  size?: "sm" | "touch";
  icon?: IconName;
  iconRight?: IconName;
  children?: ReactNode;
}

export function GxButton({
  variant = "primary",
  size,
  children,
  icon,
  iconRight,
  className,
  ...rest
}: GxButtonProps): JSX.Element {
  const cn = cls(
    "gx-btn",
    `gx-btn-${variant}`,
    size && `gx-btn-${size}`,
    className,
  );
  return (
    <button className={cn} {...rest}>
      {icon && <Icon name={icon} size={16} />}
      {children}
      {iconRight && <Icon name={iconRight} size={16} />}
    </button>
  );
}

// ── GxCard ─────────────────────────────────────────────────

export interface GxCardProps extends HTMLAttributes<HTMLDivElement> {
  dark?: boolean;
  flat?: boolean;
}

export function GxCard({
  dark,
  flat,
  className,
  children,
  ...rest
}: GxCardProps): JSX.Element {
  const cn = cls(
    "gx-card",
    dark && "gx-card-dark",
    flat && "gx-card-flat",
    className,
  );
  return (
    <div className={cn} {...rest}>
      {children}
    </div>
  );
}

// ── GxInput ────────────────────────────────────────────────

export interface GxInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  icon?: IconName | null;
  shortcut?: string;
  /** Optional label rendered as the wrapper's accessible name.
   *  When provided, the wrapper becomes a <label> with this text
   *  visually hidden but available to screen readers. */
  ariaLabel?: string;
}

export function GxInput({
  icon = "search",
  shortcut,
  ariaLabel,
  className,
  ...rest
}: GxInputProps): JSX.Element {
  return (
    <label className={cls("gx-input", className)} aria-label={ariaLabel}>
      {icon && <Icon name={icon} size={16} color="var(--text-mute)" />}
      <input {...rest} />
      {shortcut && (
        <span className="gx-mono gx-mute" style={{ fontSize: 10 }}>
          {shortcut}
        </span>
      )}
    </label>
  );
}

// ── GxBadge ────────────────────────────────────────────────

export interface GxBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: "accent" | "success" | "warning" | "neutral";
  children?: ReactNode;
}

export function GxBadge({
  variant = "neutral",
  children,
  className,
  ...rest
}: GxBadgeProps): JSX.Element {
  return (
    <span
      className={cls("gx-badge", `gx-badge-${variant}`, className)}
      {...rest}
    >
      {children}
    </span>
  );
}

// ── GxChip ─────────────────────────────────────────────────

export interface GxChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

export function GxChip({
  active,
  className,
  children,
  ...rest
}: GxChipProps): JSX.Element {
  return (
    <button
      className={cls("gx-chip", className)}
      aria-pressed={active}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── GxStat ─────────────────────────────────────────────────

export interface GxStatProps {
  label: string;
  value: string | number;
  delta?: string;
  deltaPositive?: boolean;
  icon?: IconName;
  color?: string;
}

export function GxStat({
  label,
  value,
  delta,
  deltaPositive,
  icon,
  color,
}: GxStatProps): JSX.Element {
  return (
    <div className="gx-stat">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--s-3)",
        }}
      >
        {icon && (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "var(--r-md)",
              background: color ? `${color}22` : "var(--bg-deep)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name={icon} size={18} color={color || "var(--text-dim)"} />
          </div>
        )}
        {delta && (
          <GxBadge variant={deltaPositive ? "success" : "accent"}>
            {delta}
          </GxBadge>
        )}
      </div>
      <div className="gx-stat-value">{value}</div>
      <div className="gx-stat-label">{label}</div>
    </div>
  );
}

// ── GxProductTile ──────────────────────────────────────────

export interface GxProductTileProduct {
  name: string;
  price: number;
  emoji: string;
  /** Tile background color — accepts a CSS custom-property string
   *  like "var(--brand-magenta)". */
  color: string;
  stock?: number;
}

export interface GxProductTileProps {
  product: GxProductTileProduct;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

export function GxProductTile({
  product,
  onClick,
}: GxProductTileProps): JSX.Element {
  return (
    <div
      className="gx-product"
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div
        className="gx-product-img"
        style={{
          background: `radial-gradient(circle at 35% 30%, ${product.color}, ${product.color}66)`,
        }}
      >
        <span style={{ filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.15))" }}>
          {product.emoji}
        </span>
      </div>
      <div
        style={{
          fontSize: "var(--fs-base)",
          fontWeight: 600,
          lineHeight: "var(--lh-snug)",
          minHeight: 32,
        }}
      >
        {product.name}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "var(--s-2)",
        }}
      >
        <div className="gx-display" style={{ fontSize: "var(--fs-md)" }}>
          {fmtCOP(product.price)}
        </div>
        {product.stock != null && (
          <div className="gx-mono gx-mute" style={{ fontSize: "var(--fs-xs)" }}>
            ×{product.stock}
          </div>
        )}
      </div>
    </div>
  );
}

// ── GxNavItem ──────────────────────────────────────────────

export interface GxNavItemProps {
  icon: IconName;
  label: string;
  active?: boolean;
  badge?: number | string;
  href?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export function GxNavItem({
  icon,
  label,
  active,
  badge,
  href,
  onClick,
}: GxNavItemProps): JSX.Element {
  return (
    <a
      className="gx-nav-item"
      aria-current={active ? "page" : undefined}
      href={href}
      onClick={onClick}
    >
      <Icon name={icon} size={17} />
      <span>{label}</span>
      {badge != null && (
        <span
          style={{
            marginLeft: "auto",
            background: active ? "var(--accent)" : "var(--border)",
            color: active ? "var(--accent-ink)" : "var(--text-dim)",
            fontSize: "var(--fs-xs)",
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: "var(--r-xs)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {badge}
        </span>
      )}
    </a>
  );
}

// ── GxStatusDot ────────────────────────────────────────────

export interface GxStatusDotProps {
  status: "live" | "busy" | "warn" | "offline";
}

export function GxStatusDot({ status }: GxStatusDotProps): JSX.Element {
  const color =
    status === "live"
      ? "var(--success)"
      : status === "busy"
        ? "var(--accent)"
        : status === "warn"
          ? "var(--warning)"
          : "var(--text-mute)";
  return (
    <span
      className="gx-dot"
      style={{
        background: color,
        boxShadow:
          status === "live" ? "0 0 0 3px rgba(47,163,107,0.2)" : "none",
      }}
    />
  );
}

// ── Skeleton (NEW — research R-008) ────────────────────────

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  /** When set, renders N stacked horizontal bars, each separated
   *  by --s-2. Useful for multi-line text placeholders. */
  lines?: number;
}

export function Skeleton({
  width = "100%",
  height = "1rem",
  radius = "var(--r-sm)",
  lines = 1,
  className,
  style,
  ...rest
}: SkeletonProps): JSX.Element {
  if (lines > 1) {
    return (
      <div
        className={cls("gx-skeleton-stack", className)}
        aria-busy="true"
        {...rest}
      >
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className="gx-skeleton"
            style={{
              width: i === lines - 1 ? "70%" : width,
              height,
              borderRadius: radius,
            }}
          />
        ))}
      </div>
    );
  }
  return (
    <div
      className={cls("gx-skeleton", className)}
      style={{
        width,
        height,
        borderRadius: radius,
        ...style,
      }}
      aria-busy="true"
      {...rest}
    />
  );
}

// ── GxDrawer (NEW — research R-009) ────────────────────────

export interface GxDrawerProps {
  open: boolean;
  onClose: () => void;
  side?: "left" | "right";
  /** ID of the element labelling this drawer (e.g. its heading). */
  labelledBy?: string;
  /** Show the X close button in the panel corner. Default true.
   *  Disable when the drawer's children render their own close UI. */
  showCloseButton?: boolean;
  /** Accessible label for the close button. */
  closeLabel?: string;
  /** Extra class added to the panel for visual customization
   *  (gradient backgrounds, decorative themes, etc). */
  panelClassName?: string;
  children?: ReactNode;
}

export function GxDrawer({
  open,
  onClose,
  side = "left",
  labelledBy,
  showCloseButton = true,
  closeLabel = "Cerrar menú",
  panelClassName,
  children,
}: GxDrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Esc closes; outside click handled by backdrop element.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Body scroll lock — evita el "salto" cuando aparece el drawer y
  // bloquea el scroll del fondo. La regla `scrollbar-gutter: stable`
  // en `html` mantiene el ancho consistente.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open) return;
    const root = document.documentElement;
    root.classList.add("gx-no-scroll");
    return () => root.classList.remove("gx-no-scroll");
  }, [open]);

  // Initial focus when drawer opens.
  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus();
    }
  }, [open]);

  const handleBackdropClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Renderizamos via portal a document.body para escapar cualquier
  // stacking context creado por ancestros (filter, backdrop-filter,
  // transform, etc). Sin esto, un padre con backdrop-filter atrapa
  // el `position: fixed` del backdrop y rompe el rendering.
  return createPortal(
    <div
      className="gx-drawer-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={cls("gx-drawer", `gx-drawer-${side}`, panelClassName)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {showCloseButton && (
          <button
            type="button"
            className="gx-drawer__close"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <Icon name="x" size={22} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ── GxAvatar (NEW — research R-015) ────────────────────────

export type AvatarAcento = "magenta" | "purple" | "pastel" | "star";

export interface GxAvatarProps {
  iniciales: string;
  acento?: AvatarAcento;
  size?: number;
}

const ACENTO_BG: Record<AvatarAcento, string> = {
  magenta: "var(--brand-magenta-soft)",
  purple: "var(--brand-purple-soft)",
  pastel: "var(--brand-pastel)",
  star: "var(--brand-star)",
};
const ACENTO_INK: Record<AvatarAcento, string> = {
  magenta: "var(--brand-magenta-deep)",
  purple: "var(--brand-purple-deep)",
  pastel: "var(--brand-purple-deep)",
  star: "var(--brand-cosmic)",
};

export function GxAvatar({
  iniciales,
  acento = "magenta",
  size = 40,
}: GxAvatarProps): JSX.Element {
  const trimmed = iniciales.slice(0, 2).toUpperCase();
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: "50%",
    background: ACENTO_BG[acento],
    color: ACENTO_INK[acento],
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: size * 0.4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };
  return (
    <span className="gx-avatar" style={style} aria-hidden="true">
      {trimmed}
    </span>
  );
}
